import React, { Component } from "react";
import ReactHtmlParser from "react-html-parser";
import Uppy from "@uppy/core";
import Transloadit from "@uppy/transloadit";
import { Progress } from "semantic-ui-react";
import { notification, randomNumber } from "../../../../utils/helpers";
import TextAction from "./TextAction";
import Comments from "./Comments";
import { CommentResource } from "../../../../stores/CommentStore/Types";

interface Props {
  selectedAnswer: any;
  question: any;
  answer: any;
  updateSelectedAnswer: (val: any) => void;
  submitAnswer: (e) => void;
  submitting: boolean;
  loadingAnswer: boolean;
  mode: string;
  comments: CommentResource[];
  currentUser: any;
  search: string;
  enrolmentId: number;
}

interface State {
  uploadNewImage: boolean;
  fileUrl: string;
  progress: number;
  uploadingImage: boolean;
  feedback: string;
  uploadFailed: boolean;
}

// Uploads that are going to work are done well inside this. Uploads that are
// going to fail on a locked-down corporate network mostly fail silently -- the
// request never lands, so there is no error to react to -- and without a
// deadline the learner watches a progress bar until they give up.
const UPLOAD_TIMEOUT_MS = 90000;

class FileAction extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      fileUrl: "",
      uploadNewImage: false,
      progress: 0,
      uploadingImage: false,
      feedback: "",
      uploadFailed: false,
    };

    (this as any).uppy = Uppy({ debug: true, autoProceed: true });

    const templateId =
      props.question.uploader_type === "image"
        ? process.env.TRANSLOADIT_IMAGE_TEMPLATE_ID
        : process.env.TRANSLOADIT_FILE_TEMPLATE_ID;

    (this as any).uppy
      .use(Transloadit, {
        params: {
          auth: {
            key: process.env.TRANSLOADIT_AUTH_KEY,
            secret: process.env.TRANSLOADIT_AUTH_SECRET,
            duration: 1800,
          },
          template_id: templateId,
        },
        waitForEncoding: true,
      })
      .on("error", (error) => {
        if (error.assembly) {
          console.log(`Assembly ID ${error.assembly.assembly_id} failed!`);
          console.log(error.assembly);
        }
        this.failUpload();
      })
      .on("transloadit:complete", (assembly) => {
        this.finalizeUpload(assembly);
      })
      .on("progress", (progress) => {
        this.setState({ progress: progress });
      });
  }

  componentWillUnmount() {
    this.clearUploadTimeout();
  }

  clearUploadTimeout = () => {
    if ((this as any).uploadTimer) {
      clearTimeout((this as any).uploadTimer);
      (this as any).uploadTimer = null;
    }
  };

  failUpload = () => {
    this.clearUploadTimeout();
    this.setState({ uploadingImage: false, progress: 0, uploadFailed: true });
  };

  addFile(file) {
    const descriptor = {
      id: "FileInput",
      name: randomNumber() + file.name,
      type: file.type,
      data: file,
    };

    try {
      (this as any).uppy.addFile(descriptor);
    } catch (err) {
      this.setState({ uploadingImage: false });
      console.log(err);

      (this as any).uppy.log(err);

      notification("Error uploading image. Please try again", "error");
      return;
    }
  }

  uploadFile = (e) => {
    (this as any).uppy.log("[FileInput] Something selected through input...");
    const file = e.target.files[0];
    if (!file) return;

    this.setState({
      uploadingImage: true,
      progress: 50,
      feedback: "",
      uploadFailed: false,
    });

    this.clearUploadTimeout();
    (this as any).uploadTimer = setTimeout(this.failUpload, UPLOAD_TIMEOUT_MS);

    this.addFile(file);

    e.target.value = null;
  };

  finalizeUpload = (assembly) => {
    const { question } = this.props;
    let assemblyResult;

    const resultKey =
      question.uploader_type === "image" ? "resize" : ":original";

    const heic = assembly.uploads[0].ext == "heic";

    if (question.uploader_type == "image" && heic) {
      assemblyResult = assembly.results["resize_heic"][0];
    } else {
      assemblyResult = assembly.results[resultKey][0];
    }

    const resultSslUrl = assemblyResult.ssl_url;
    this.clearUploadTimeout();
    this.setState({
      fileUrl: resultSslUrl,
      uploadingImage: false,
      feedback: "",
      uploadFailed: false,
    });
    this.props.updateSelectedAnswer(resultSslUrl);
  };

  displayFile = (url) => {
    const { question } = this.props;

    if (question.uploader_type == "image") {
      return (
        <a href={url} target="_blank">
          <img src={url} className="ui small image" />
        </a>
      );
    } else {
      return (
        <p>
          <a href={url} target="_blank">
            <i className="icon attach" />

            {url && <>{url.substring(url.lastIndexOf("/") + 1)}</>}
          </a>
        </p>
      );
    }
  };

  undoFileUpload = () => {
    this.setState({ fileUrl: "", progress: 0 });
  };

  validateFileUpload = (e) => {
    if (!this.state.fileUrl) {
      e.preventDefault();
      this.setState({ feedback: "Upload a file before submitting." });
      return;
    }

    this.props.submitAnswer(e);
  };

  render() {
    const {
      submitAnswer,
      question,
      answer,
      submitting,
      loadingAnswer,
      selectedAnswer,
      updateSelectedAnswer,
      comments,
      currentUser,
      mode,
      search,
      enrolmentId,
    } = this.props;

    const { fileUrl, uploadingImage, progress, feedback, uploadFailed } =
      this.state;
    const translation = question?.translations?.find(
      (t) => t.language === currentUser?.locale,
    );
    return (
      <>
        {question.uploader_type ? (
          <div className="q-card assignment-form">
            <div className="q-row">
              <span className="q-badge">Q</span>
              <div className="q-text">
                {ReactHtmlParser(
                  translation?.formatted_description ||
                    question?.formatted_description,
                )}
                {question.uploader_type === "image" && (
                  <p className="q-hint">
                    Only upload images (.jpeg, .jpg, .png, .gif).
                  </p>
                )}
              </div>
            </div>
            <div className="q-row q-answer">
              {(loadingAnswer || submitting) && (
                <div className="ui active inverted dimmer">
                  <div className="ui text loader">Loading</div>
                </div>
              )}
              <span className="q-badge a">A</span>
              <div className="q-answer__body">
                {answer && (
                  <div className="file-container">
                    {this.displayFile(answer.content_url)}
                  </div>
                )}

                {answer || fileUrl ? (
                  <></>
                ) : (
                  <div className="upload-file">
                    {!uploadingImage && (
                      <div className="field file optional uploader_transloadit">
                        <form id="upload-form">
                          <label
                            className="btn btn-outline btn--compact q-upload file optional"
                            htmlFor="uploader_transloadit"
                          >
                            <svg aria-hidden="true">
                              <use href="#i-upload" />
                            </svg>
                            Upload {question.uploader_type}
                          </label>
                          {question.uploader_type === "image" ? (
                            <input
                              className="file optional"
                              type="file"
                              name="uploader[transloadit]"
                              id="uploader_transloadit"
                              onChange={this.uploadFile}
                              accept="image/*"
                              style={{ display: "none" }}
                            />
                          ) : (
                            <input
                              className="file optional"
                              type="file"
                              name="uploader[transloadit]"
                              id="uploader_transloadit"
                              onChange={this.uploadFile}
                              style={{ display: "none" }}
                            />
                          )}
                        </form>
                      </div>
                    )}
                    {uploadingImage && (
                      <Progress
                        percent={progress}
                        progress="percent"
                        size="small"
                        indicating
                        active
                      >
                        Uploading...
                      </Progress>
                    )}
                    {/* Nearly every upload that fails does so on a work network
                        that blocks them outright, so the help leads with that
                        rather than asking the learner to try the same thing
                        again and watch it hang for another minute and a half. */}
                    {uploadFailed && (
                      <div className="q-upload-fail" role="alert">
                        <p className="q-upload-fail__lede">
                          That upload did not go through.
                        </p>
                        <p>
                          Nine times out of ten this is a work computer or
                          network blocking uploads rather than anything wrong
                          with your file. Any of these will get you past it:
                        </p>
                        <ul>
                          <li>
                            Try again on a personal laptop, or on your phone
                            using mobile data rather than the office wifi.
                          </li>
                          <li>
                            Ask your IT team to allow uploads to 42courses.com
                            through the firewall.
                          </li>
                          <li>
                            Email the file to{" "}
                            <a href="mailto:groundcontrol@42courses.com?subject=Course%20answer%20upload">
                              groundcontrol@42courses.com
                            </a>{" "}
                            and we will attach it to your answer by hand.
                          </li>
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                {fileUrl && !answer && (
                  <div className="file-container">
                    {this.displayFile(fileUrl)}
                  </div>
                )}
                {/* "Mark as passed immediately" in admin: nobody marks it. */}
                <p className="q-hint">
                  Accepted: PDF, Word, PowerPoint, or an image.
                  {!question.auto_pass && " We mark every submission by hand."}
                </p>
              </div>
            </div>
            {!answer && (
              <div className="q-actions">
                <span className={`q-feedback ${feedback ? "no" : ""}`}>
                  {feedback}
                </span>
                {fileUrl && (
                  <button
                    className="btn btn-outline btn--compact"
                    type="button"
                    onClick={this.undoFileUpload}
                  >
                    Undo
                  </button>
                )}
                <button
                  className={`btn btn-blue btn--compact ${
                    submitting ? "loading" : ""
                  }`}
                  type="submit"
                  onClick={this.validateFileUpload}
                >
                  Submit answer
                </button>
              </div>
            )}

            {mode === "Opinion" && answer && answer.state === "passed" && (
              <Comments
                comments={comments}
                answer={answer}
                currentUser={currentUser}
                search={search}
                enrolmentId={enrolmentId}
                questionId={question?.id}
              />
            )}
          </div>
        ) : (
          <TextAction
            question={question}
            answer={answer}
            submitAnswer={submitAnswer}
            selectedAnswer={selectedAnswer}
            submitting={submitting}
            loadingAnswer={loadingAnswer}
            updateSelectedAnswer={updateSelectedAnswer}
          />
        )}
      </>
    );
  }
}

export default FileAction;
